






















































import Vue from "vue";
import { PropType } from "vue";
import {
   BewegungRecord,
   DatePaginationMeta,
} from "@/models/ConvenienceBestandModel";
import { catVue } from "@/util/logging";

declare global {
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
   interface Window {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Chart: any;
   }
}

export default Vue.extend({
   name: "BewegungChart",

   props: {
      bewegungen: {
         type: Array as PropType<BewegungRecord[]>,
         required: true,
      },
      meta: {
         type: Object as PropType<DatePaginationMeta>,
         required: true,
      },
      isLoading: {
         type: Boolean,
         default: false,
      },
      defaultDateRange: {
         type: Object as PropType<{ von: string; bis: string }>,
         required: true,
      },
      currentDateRange: {
         type: Object as PropType<{ von: string; bis: string }>,
         required: true,
      },
      suppressAutoLoad: {
         type: Boolean,
         default: false,
      },
   },

   data: () => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      chart: null as any,
      chartLoaded: false,
      autoLoadInProgress: false,
   }),

   mounted() {
      catVue.info(
         `[BewegungChart] MOUNTED, bewegungen.length=${
            this.bewegungen.length
         }, meta=${!!this.meta}`,
      );
      this.loadChartLibrary();
   },

   watch: {
      bewegungen: {
         handler(newVal, oldVal) {
            catVue.info(
               `[BewegungChart] bewegungen changed: ${oldVal?.length} -> ${newVal?.length}`,
            );
            if (this.chartLoaded) {
               this.$nextTick(() => {
                  this.renderChart();
               });
            }
         },
         deep: true,
      },
      meta: {
         handler(newVal, oldVal) {
            catVue.info(
               `[BewegungChart] meta changed: old=${!!oldVal}, new=${!!newVal}`,
            );
            if (
               this.chartLoaded &&
               this.bewegungen &&
               this.bewegungen.length > 0
            ) {
               this.$nextTick(() => {
                  this.renderChart();
               });
            }
         },
         deep: true,
      },
      isLoading: {
         handler(newVal, oldVal) {
            // When loading completes, check if we need more data
            if (oldVal === true && newVal === false) {
               catVue.info(
                  "[BewegungChart] Loading complete, checking if more data needed",
               );
               this.$nextTick(() => {
                  this.checkAndLoadPreviousIfNeeded();
               });
            }
         },
      },
   },

   methods: {
      loadChartLibrary() {
         if (window.Chart) {
            this.chartLoaded = true;
            this.$nextTick(() => {
               this.renderChart();
            });
         } else {
            // Load Chart.js from CDN if not already loaded
            const script = document.createElement("script");
            script.src =
               "https://cdn.jsdelivr.net/npm/chart.js@3/dist/chart.min.js";
            script.onload = () => {
               this.chartLoaded = true;
               this.$nextTick(() => {
                  this.renderChart();
               });
            };
            document.head.appendChild(script);
         }
      },

      renderChart() {
         catVue.info(
            `[BewegungChart] renderChart called, bewegungen.length=${
               this.bewegungen.length
            }, meta=${!!this.meta}`,
         );
         const canvas = this.$refs.chartCanvas as HTMLCanvasElement;
         if (!canvas) {
            catVue.warn("[BewegungChart] No canvas ref found");
            return;
         }

         catVue.info(
            `[BewegungChart] Canvas found, ${canvas.offsetWidth}x${canvas.offsetHeight}px`,
         );

         // Destroy existing chart if it exists
         if (this.chart) {
            catVue.info("[BewegungChart] Destroying existing chart");
            this.chart.destroy();
         }

         // Format dates from YYYY-MM-DD to DD.MM.YY
         const formatDate = (isoDate: string): string => {
            const parts = isoDate.split("-");
            if (parts.length !== 3) return isoDate;
            const year = parts[0].slice(-2); // Last 2 digits of year
            return `${parts[2]}.${parts[1]}.${year}`;
         };

         const labels = this.bewegungen.map((b) => formatDate(b.date));
         const zugangData = this.bewegungen.map((b) => b.zugang);
         const abgangData = this.bewegungen.map((b) => b.abgang);
         const bestandData = this.bewegungen.map((b) => b.bestand);

         const ctx = canvas.getContext("2d");
         if (!ctx) return;

         this.chart = new window.Chart(ctx, {
            type: "bar",
            data: {
               labels,
               datasets: [
                  {
                     label: "Zugang (Lieferung)",
                     data: zugangData,
                     borderColor: "#4CAF50",
                     backgroundColor: "rgba(76, 175, 80, 0.5)",
                     yAxisID: "y",
                     type: "bar",
                     order: 2,
                  },
                  {
                     label: "Abgang (Verkauf)",
                     data: abgangData,
                     borderColor: "#F44336",
                     backgroundColor: "rgba(244, 67, 54, 0.5)",
                     yAxisID: "y",
                     type: "bar",
                     order: 3,
                  },
                  {
                     label: "Bestand",
                     data: bestandData,
                     borderColor: "#2196F3",
                     backgroundColor: "rgba(33, 150, 243, 0.1)",
                     borderWidth: 2,
                     yAxisID: "y1",
                     type: "line",
                     tension: 0.4,
                     pointRadius: 4,
                     pointHoverRadius: 6,
                     order: 1,
                  },
               ],
            },
            options: {
               responsive: true,
               maintainAspectRatio: true,
               interaction: {
                  mode: "index",
                  intersect: false,
               },
               plugins: {
                  legend: {
                     position: "top",
                  },
                  title: {
                     display: false,
                  },
               },
               scales: {
                  y: {
                     type: "linear",
                     display: true,
                     position: "left",
                     title: {
                        display: true,
                        text: "Menge (Zu-/Abgang)",
                     },
                     min: 0,
                  },
                  y1: {
                     type: "linear",
                     display: true,
                     position: "right",
                     title: {
                        display: true,
                        text: "Bestand",
                     },
                     grid: {
                        drawOnChartArea: false,
                     },
                     min: 0,
                  },
               },
            },
         });

         // Auto-load previous data if columns are too narrow
         this.$nextTick(() => {
            this.checkAndLoadPreviousIfNeeded();
         });
      },

      checkAndLoadPreviousIfNeeded() {
         // Don't auto-load if suppressed (during filiale switch), already loading, or no data
         if (
            this.suppressAutoLoad ||
            this.autoLoadInProgress ||
            !this.chart ||
            !this.meta ||
            !this.meta._links.prev ||
            !this.bewegungen ||
            this.bewegungen.length === 0
         ) {
            catVue.info(
               `[BewegungChart] checkAndLoadPreviousIfNeeded skipped: suppressAutoLoad=${
                  this.suppressAutoLoad
               }, autoLoadInProgress=${this.autoLoadInProgress}, hasData=${
                  !!this.bewegungen && this.bewegungen.length > 0
               }, hasPrev=${!!this.meta?._links.prev}`,
            );
            return;
         }

         // Get canvas and container dimensions
         const canvas = this.$refs.chartCanvas as HTMLCanvasElement;
         if (!canvas || !canvas.parentElement) {
            return;
         }

         // Calculate actual column width based on container width, not canvas width
         const containerWidth = canvas.parentElement.offsetWidth;
         const numColumns = this.bewegungen.length;
         const columnWidth = containerWidth / numColumns;

         // If columns are smaller than 25px and we have previous data, load it
         if (columnWidth < 25) {
            catVue.info(
               `Auto-loading previous: columnWidth=${columnWidth.toFixed(
                  1,
               )}px < 25px, ${numColumns} columns`,
            );
            this.autoLoadInProgress = true;
            this.$emit("load-prev");
            // Reset flag after a delay to allow new data to arrive
            setTimeout(() => {
               this.autoLoadInProgress = false;
            }, 1000);
         }
      },

      loadPreviousPeriod() {
         this.$emit("load-prev");
      },

      loadNextPeriod() {
         this.$emit("load-next");
      },

      formatDateRange(): string {
         if (!this.meta) return "";
         const formatDate = (isoDate: string): string => {
            const parts = isoDate.split("-");
            if (parts.length !== 3) return isoDate;
            return `${parts[2]}.${parts[1]}.`; // DD.MM.
         };
         const start = formatDate(this.meta.dateRangeStart);
         const endFull = this.meta.dateRangeEnd.split("-");
         const endFormatted = `${endFull[2]}.${endFull[1]}.${endFull[0]}`; // DD.MM.YYYY
         return `${start} – ${endFormatted}`;
      },

      isCurrentWeekRange(): boolean {
         // Reset button is disabled when we're back at the default range
         if (
            !this.meta ||
            !this.defaultDateRange.von ||
            !this.defaultDateRange.bis
         )
            return false;

         const isAtDefault =
            this.meta.dateRangeStart === this.defaultDateRange.von &&
            this.meta.dateRangeEnd === this.defaultDateRange.bis;

         catVue.info(
            `[BewegungChart] isCurrentWeekRange: current=[${this.meta.dateRangeStart}, ${this.meta.dateRangeEnd}], default=[${this.defaultDateRange.von}, ${this.defaultDateRange.bis}], isAtDefault=${isAtDefault}`,
         );
         return isAtDefault;
      },

      isResetDisabled(): boolean {
         return this.isCurrentWeekRange();
      },

      resetPeriod() {
         this.$emit("reset-period");
      },
   },
});
